import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Clock, RefreshCw, ChevronDown, ExternalLink, AlertTriangle, X, Shield } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { StripeAccountService } from '@/services/stripe/stripe-account-service';
import { StripeExpressDashboardService } from '@/services/stripe/stripe-express-dashboard-service';
import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';
import { useOrgaConfigurationContext } from '@/app/providers';
// Note: CommissionField a été déplacé vers Plan & Facturation

interface StripeHostedOnboardingSimpleProps {
  organizationId: string;
  stripeAccountId?: string;
  stripeStatus?: 'pending' | 'active' | 'suspended' | 'rejected';
  onStatusChange?: (status: string) => void;
  onAccountCreated?: (accountId: string) => void;
  onAccountInfoRetrieved?: (accountInfo: any) => void;
  onUpdateCommission?: (newRate: number, newFixedCommission?: number) => Promise<void>;
  isCommissionLoading?: boolean;
  isSuperAdmin?: boolean;
}

const StripeHostedOnboardingSimple: React.FC<StripeHostedOnboardingSimpleProps> = ({
  organizationId,
  stripeAccountId: propStripeAccountId,
  stripeStatus: propStripeStatus,
  onAccountCreated,
  onAccountInfoRetrieved,
  isSuperAdmin = false
}) => {
  const { t } = useTranslation();
  const { configuration } = useOrgaConfigurationContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false); // État pour éviter les créations multiples
  const [error, setError] = useState<string | null>(null);
  const [stripeStatus, setStripeStatus] = useState<'complete' | 'pending' | 'incomplete' | null>(
    propStripeStatus === 'active' ? 'complete' : (propStripeStatus === 'pending' ? 'pending' : null)
  );
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(propStripeAccountId || null);
  const [validationDetails, setValidationDetails] = useState<any>(null);

  // Synchroniser l'état local avec les props quand elles changent
  useEffect(() => {
    if (propStripeAccountId && !stripeAccountId) {
      setStripeAccountId(propStripeAccountId);
    }
    if (propStripeStatus && (stripeStatus === null)) {
      setStripeStatus(propStripeStatus === 'active' ? 'complete' : (propStripeStatus === 'pending' ? 'pending' : null));
    }
  }, [propStripeAccountId, propStripeStatus]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isOpeningDashboard, setIsOpeningDashboard] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showPastDue, setShowPastDue] = useState(true);
  const [showCurrentlyDue, setShowCurrentlyDue] = useState(false);
  const [showEventuallyDue, setShowEventuallyDue] = useState(false);
  const hasCheckedRef = useRef(false);

  // Traduction des éléments Stripe
  const translateStripeElement = (element: string) => {
    const translations: Record<string, string> = {
      'external_account': t('admin.configuration.stripe.fields.external_account', 'Compte bancaire'),
      'tos_acceptance.date': t('admin.configuration.stripe.fields.tos_acceptance_date', 'Date d\'acceptation CGU'),
      'tos_acceptance.ip': t('admin.configuration.stripe.fields.tos_acceptance_ip', 'Adresse IP acceptation CGU'),
      'business_profile.url': t('admin.configuration.stripe.fields.business_profile_url', 'URL du site web'),
      'business_profile.mcc': t('admin.configuration.stripe.fields.business_profile_mcc', 'Code de catégorie marchande'),
      'individual.first_name': t('admin.configuration.stripe.fields.first_name', 'Prénom'),
      'individual.last_name': t('admin.configuration.stripe.fields.last_name', 'Nom de famille'),
      'individual.email': t('admin.configuration.stripe.fields.email', 'Email'),
      'individual.phone': t('admin.configuration.stripe.fields.phone', 'Téléphone'),
      'individual.address.line1': t('admin.configuration.stripe.fields.address', 'Adresse'),
      'individual.address.city': t('admin.configuration.stripe.fields.city', 'Ville'),
      'individual.address.postal_code': t('admin.configuration.stripe.fields.postal_code', 'Code postal'),
      'individual.address.country': t('admin.configuration.stripe.fields.country', 'Pays'),
      'individual.dob.day': t('admin.configuration.stripe.fields.dob_day', 'Jour de naissance'),
      'individual.dob.month': t('admin.configuration.stripe.fields.dob_month', 'Mois de naissance'),
      'individual.dob.year': t('admin.configuration.stripe.fields.dob_year', 'Année de naissance'),
      'individual.id_number': t('admin.configuration.stripe.fields.id_number', 'Numéro d\'identité'),
      'company.name': t('admin.configuration.stripe.fields.company_name', 'Nom de l\'entreprise'),
      'company.address.line1': t('admin.configuration.stripe.fields.company_address', 'Adresse entreprise'),
      'company.phone': t('admin.configuration.stripe.fields.company_phone', 'Téléphone entreprise'),
      'company.tax_id': t('admin.configuration.stripe.fields.tax_id', 'Numéro SIRET/TVA'),
      'company.verification.document': t('admin.configuration.stripe.fields.company_document', 'Document d\'entreprise'),
      'individual.verification.document': t('admin.configuration.stripe.fields.id_document', 'Pièce d\'identité'),
      'individual.verification.additional_document': t('admin.configuration.stripe.fields.additional_document', 'Document supplémentaire')
    };
    return translations[element] || element;
  };

  // Vérifier le statut initial
  useEffect(() => {
    const stripeAccountIdFromConfig = configuration?.stripeConnectConfig?.accountId || configuration?.stripeConfig?.stripeAccountId;
    console.log('🔄 useEffect déclenché - configuration:', stripeAccountIdFromConfig);

    const checkInitialStatus = async () => {
      try {
        // Récupérer l'accountId de Stripe depuis les props ou la configuration
        const stripeAccountId = propStripeAccountId || stripeAccountIdFromConfig;

        if (stripeAccountId && !hasCheckedRef.current) {
          hasCheckedRef.current = true;
          setIsChecking(true);
          console.log('🔍 Vérification statut Stripe avec accountId:', stripeAccountId);
          const response = await StripeAccountService.checkAccountStatus(stripeAccountId);
          console.log('📊 Réponse API Stripe:', response);

          if (response && response.accountId) {
            console.log('✅ Données Stripe récupérées:', {
              accountId: response.accountId,
              status: response.status,
              chargesEnabled: response.chargesEnabled,
              payoutsEnabled: response.payoutsEnabled
            });

            // ✅ IMPORTANT : Toujours mettre à jour l'état local pour l'affichage UI
            // Même si on ne synchronise pas avec Firestore
            setStripeAccountId(response.accountId);
            setStripeStatus(response.status === 'active' ? 'complete' : 'pending');
            setValidationDetails({
              chargesEnabled: response.chargesEnabled,
              payoutsEnabled: response.payoutsEnabled,
              detailsSubmitted: response.detailsSubmitted,
              currentlyDue: response.requirements?.currentlyDue || [],
              eventuallyDue: response.requirements?.eventuallyDue || [],
              pastDue: response.requirements?.pastDue || [],
              disabledReason: response.requirements?.disabledReason,
              pendingVerification: []
            });

            // Synchroniser avec Firestore UNIQUEMENT si les données diffèrent
            const config = configuration?.stripeConnectConfig;
            const hasChanged = !config ||
              config.status !== (response.status === 'active' ? 'active' : 'pending') ||
              config.chargesEnabled !== response.chargesEnabled ||
              config.payoutsEnabled !== response.payoutsEnabled ||
              config.detailsSubmitted !== response.detailsSubmitted;

            if (hasChanged && onAccountInfoRetrieved) {
              console.log('💾 Synchronisation avec Firestore car les données ont changé');
              onAccountInfoRetrieved({
                id: response.accountId,
                charges_enabled: response.chargesEnabled,
                payouts_enabled: response.payoutsEnabled,
                details_submitted: response.detailsSubmitted,
                requirements: response.requirements
              });
            } else {
              console.log('⏭️ Pas de synchronisation nécessaire avec Firestore, mais UI mise à jour');
            }
          }
        } else if (!stripeAccountId) {
          console.log('⚠️ Aucun accountId Stripe trouvé dans la configuration');
        } else {
          console.log('⏭️ Vérification déjà effectuée, skip');
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut initial:', error);
      } finally {
        setIsChecking(false);
      }
    };

    if (configuration || propStripeAccountId) {
      checkInitialStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration, organizationId, propStripeAccountId, propStripeStatus]);

  // Actualiser les données Stripe
  const refreshStripeData = async () => {
    if (!stripeAccountId) return;

    setIsRefreshing(true);
    hasCheckedRef.current = false; // Réinitialiser pour permettre une nouvelle vérification
    try {
      console.log('🔄 Actualisation des données Stripe...');
      const response = await StripeAccountService.checkAccountStatus(stripeAccountId);

      if (response && response.accountId) {
        // ✅ Mapper les statuts : active → complete, tout le reste → pending
        setStripeStatus(response.status === 'active' ? 'complete' : 'pending');

        setValidationDetails({
          chargesEnabled: response.chargesEnabled,
          payoutsEnabled: response.payoutsEnabled,
          detailsSubmitted: response.detailsSubmitted,
          currentlyDue: response.requirements?.currentlyDue || [],
          eventuallyDue: response.requirements?.eventuallyDue || [],
          pastDue: response.requirements?.pastDue || [],
          disabledReason: response.requirements?.disabledReason,
          pendingVerification: (response.requirements as any)?.pending_verification || []
        });

        // Synchroniser avec Firestore UNIQUEMENT si les données diffèrent
        const configConnect = configuration?.stripeConnectConfig;
        const configLegacy = configuration?.stripeConfig;

        const currentChargesEnabled = configConnect?.chargesEnabled ?? configLegacy?.chargesEnabled;
        const currentPayoutsEnabled = configConnect?.payoutsEnabled ?? configLegacy?.payoutsEnabled;
        const currentDetailsSubmitted = configConnect?.detailsSubmitted ?? configLegacy?.detailsSubmitted;
        const currentStatus = configConnect?.status ?? configLegacy?.stripeStatus;

        const hasChanged = !configConnect ||
          currentStatus !== (response.status === 'active' ? 'active' : 'pending') ||
          currentChargesEnabled !== response.chargesEnabled ||
          currentPayoutsEnabled !== response.payoutsEnabled ||
          currentDetailsSubmitted !== response.detailsSubmitted;

        if (hasChanged && onAccountInfoRetrieved) {
          console.log('💾 Synchronisation avec Firestore car les données ont changé');
          onAccountInfoRetrieved({
            id: response.accountId,
            charges_enabled: response.chargesEnabled,
            payouts_enabled: response.payoutsEnabled,
            details_submitted: response.detailsSubmitted,
            requirements: response.requirements
          });
        }

        toast.success(t('admin.configuration.stripe.dataRefreshed'));
        console.log('✅ Données Stripe actualisées');
      }
    } catch (error) {
      console.error('Erreur lors de l\'actualisation:', error);
      toast.error(t('admin.configuration.stripe.refreshError'));
    } finally {
      setIsRefreshing(false);
    }
  };

  // Ouvrir le Dashboard Express Stripe
  const handleOpenExpressDashboard = async () => {
    if (!stripeAccountId) {
      console.error('❌ Aucun compte Stripe configuré');
      return;
    }

    setIsOpeningDashboard(true);
    try {
      await StripeExpressDashboardService.openExpressDashboard(stripeAccountId);
    } finally {
      setIsOpeningDashboard(false);
    }
  };

  // Gérer le retour de l'onboarding Stripe
  const handleStripeReturn = async (accountId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('🔄 Vérification du statut du compte Stripe:', accountId);

      // Vérifier le statut du compte avec l'accountId de Stripe
      const statusResponse = await StripeAccountService.checkAccountStatus(accountId);

      if (statusResponse && statusResponse.accountId) {
        console.log('📊 Statut du compte:', statusResponse.status);

        if (statusResponse.status === 'active') {
          setStripeStatus('complete');
          setStripeAccountId(statusResponse.accountId);
          toast.success(t('admin.configuration.stripe.configComplete'));
        } else {
          setStripeStatus('pending');
          toast(t('admin.configuration.stripe.configPending'), { icon: 'ℹ️' });
        }
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du statut:', error);
      setError(t('admin.configuration.stripe.statusCheckError'));
      toast.error(t('admin.configuration.stripe.statusCheckError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Ouvrir la modale de confirmation
  const handleStartOnboardingClick = () => {
    // Vérifier si un compte existe déjà
    const stripeAccountId = propStripeAccountId || configuration?.stripeConnectConfig?.accountId || configuration?.stripeConfig?.stripeAccountId;

    if (!stripeAccountId) {
      // Si aucun compte n'existe, afficher la modale de confirmation
      setShowConfirmationModal(true);
    } else {
      // Si un compte existe déjà, démarrer directement l'onboarding
      startOnboarding();
    }
  };

  // Démarrer l'onboarding Stripe
  const startOnboarding = async () => {
    // Empêcher les créations multiples
    if (isCreating) {
      console.log('⚠️ Création de compte déjà en cours...');
      toast('Création en cours, veuillez patienter...', { icon: '⏳' });
      return;
    }

    // Fermer la modale si elle est ouverte
    setShowConfirmationModal(false);

    setIsLoading(true);
    setIsCreating(true);
    setError(null);

    try {
      // Récupérer l'accountId de Stripe depuis les props ou la configuration
      const stripeAccountId = propStripeAccountId || configuration?.stripeConnectConfig?.accountId || configuration?.stripeConfig?.stripeAccountId;

      // Vérifier à nouveau si un compte existe déjà (double vérification)
      if (stripeAccountId) {
        console.log('✅ Compte Stripe existe déjà:', stripeAccountId);
        // Créer juste un lien d'onboarding pour le compte existant
      } else {
        console.log('🆕 Aucun compte existant, création nécessaire...');
      }

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('stripe_return', 'true');

      const refreshUrl = new URL(window.location.href);
      refreshUrl.searchParams.set('stripe_refresh', 'true');

      // Si aucun compte Stripe n'existe, on le crée avec le lien d'onboarding
      if (!stripeAccountId) {
        console.log('🆕 Création d\'un nouveau compte Stripe avec onboarding pour organizationId:', organizationId);

        const response = await StripeAccountService.createAccountWithLink({
          organizationId: organizationId,
          returnUrl: currentUrl.toString(),
          refreshUrl: refreshUrl.toString(),
          // Utiliser les informations de configuration si disponibles
          country: configuration?.legalInfo?.address?.country || configuration?.address?.country || 'FR',
          email: configuration?.email || `onboarding-${organizationId}@example.com`,
          businessType: 'company',
          businessProfile: {
            name: configuration?.name || 'Espace de vente',
            url: undefined // Pas de propriété website dans OrgaConfiguration
          }
        });

        console.log('✅ Compte Stripe créé:', response.accountId);

        // Sauvegarder le nouvel accountId dans Firestore
        if (onAccountCreated) {
          await onAccountCreated(response.accountId);
        }

        // Rediriger vers l'onboarding Stripe
        if (response.onboardingUrl) {
          // Réinitialiser le flag avant la redirection
          setIsCreating(false);
          console.log('🔄 Redirection vers Stripe...');
          window.location.href = response.onboardingUrl;
          return; // Sortir de la fonction
        }
      } else {
        // Créer un lien d'onboarding pour un compte existant
        console.log('🔗 Création du lien d\'onboarding pour accountId:', stripeAccountId);

        const response = await fetch(`${FUNCTIONS_BASE_URL}/createStripeAccountLink`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            accountId: stripeAccountId,
            returnUrl: currentUrl.toString(),
            refreshUrl: refreshUrl.toString(),
          }),
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la création du lien d\'onboarding');
        }

        const data = await response.json();
        console.log('📋 Réponse du backend:', data);

        // Le backend peut retourner soit { success, accountLink } soit { url, expiresAt }
        const onboardingUrl = data.accountLink || data.url;

        if (onboardingUrl) {
          console.log('✅ Lien d\'onboarding créé, redirection vers:', onboardingUrl);

          // Redirection immédiate (pas de délai, pas de toast qui ralentit)
          setIsCreating(false); // Réinitialiser le flag
          window.location.href = onboardingUrl;
          return; // Important : sortir de la fonction avant le catch
        } else {
          throw new Error(data.error || 'Erreur lors de la création du lien');
        }
      }
    } catch (error) {
      console.error('Erreur lors du démarrage de l\'onboarding:', error);
      setError(error instanceof Error ? error.message : t('admin.configuration.stripe.onboardingStartError'));
      toast.error(t('admin.configuration.stripe.onboardingStartError'));
      // En cas d'erreur, réinitialiser le flag pour permettre une nouvelle tentative
      setIsCreating(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Vérifier si l'utilisateur revient de Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stripeReturn = urlParams.get('stripe_return');
    const accountId = urlParams.get('account_id');

    if (stripeReturn === 'true' && accountId) {
      handleStripeReturn(accountId);

      // Nettoyer l'URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('stripe_return');
      newUrl.searchParams.delete('account_id');
      window.history.replaceState({}, document.title, newUrl.pathname + newUrl.search);
    }
  }, []);

  // Afficher le loader pendant la vérification initiale
  if (isChecking) {
    return (
      <div className="bg-surface rounded-xl border border-border p-8 shadow-sm flex flex-col items-center justify-center space-y-4 min-h-64">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
        <p className="text-foreground-secondary">{t('admin.configuration.stripe.checkingStatus')}</p>
      </div>
    );
  }

  if (stripeStatus === 'complete') {
    console.log('🎉 Affichage du statut complet Stripe - Interface moderne');
    return (
      <div className="space-y-6">
        {/* Statut actif - Design moderne et épuré */}
        <div className="bg-surface border border-border rounded-2xl p-4 sm:p-8 shadow-sm">
          {/* En-tête */}
          <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-success to-success-dark rounded-2xl flex items-center justify-center shadow-lg shadow-success-20 flex-shrink-0">
              <CheckCircle className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0 w-full">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-2">
                <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                  {t('admin.configuration.stripe.accountFullyValidated')}
                </h3>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 flex-shrink-0 w-full sm:w-auto">
                  <button
                    onClick={handleOpenExpressDashboard}
                    disabled={isOpeningDashboard}
                    className="px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all text-xs font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-sm w-full sm:w-auto"
                    title={t('admin.configuration.stripe.viewDashboard')}
                  >
                    <ExternalLink className="w-3 h-3 mr-1.5" />
                    {t('admin.configuration.stripe.dashboard')}
                  </button>
                  <button
                    onClick={refreshStripeData}
                    disabled={isRefreshing}
                    className="flex items-center justify-center space-x-1.5 px-3 py-1.5 bg-background-tertiary hover:bg-background-secondary text-foreground rounded-lg transition-all text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                  >
                    {isRefreshing ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-foreground-secondary border-t-transparent"></div>
                        <span>{t('admin.configuration.stripe.refreshing')}</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3" />
                        <span>{t('admin.configuration.stripe.refresh')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
              <p className="text-foreground-secondary font-mono text-xs sm:text-sm break-all">
                ID: {stripeAccountId}
              </p>
            </div>
          </div>

          {/* Séparateur */}
          <div className="border-t border-border-light my-6"></div>

          {/* Statut des capacités - Design épuré */}
          <div>
            <h4 className="text-sm font-semibold text-foreground-secondary uppercase tracking-wide mb-4">{t('admin.configuration.stripe.capabilities')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative overflow-hidden rounded-xl p-4 bg-success-10 border-2 border-success-20">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-success rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-foreground-secondary uppercase tracking-wide">{t('admin.configuration.stripe.charges')}</div>
                    <div className="text-base font-semibold text-success-dark">{t('admin.configuration.stripe.chargesEnabled')}</div>
                  </div>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-xl p-4 bg-success-10 border-2 border-success-20">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-success rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-foreground-secondary uppercase tracking-wide">{t('admin.configuration.stripe.payouts')}</div>
                    <div className="text-base font-semibold text-success-dark">{t('admin.configuration.stripe.payoutsEnabled')}</div>
                  </div>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-xl p-4 bg-success-10 border-2 border-success-20">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-success rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-foreground-secondary uppercase tracking-wide">{t('admin.configuration.stripe.details')}</div>
                    <div className="text-base font-semibold text-success-dark">{t('admin.configuration.stripe.detailsSubmitted')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Note: Les commissions sont maintenant affichées dans Plan & Facturation */}

        {/* Exigences en attente - Design compact */}
        {validationDetails && (validationDetails.currentlyDue?.length > 0 || validationDetails.eventuallyDue?.length > 0 || validationDetails.pastDue?.length > 0) && (
          <div className="bg-surface rounded-xl border border-border p-6 shadow-sm">
            <h4 className="text-lg font-semibold text-foreground mb-4">{t('admin.configuration.stripe.requirements')}</h4>
            <div className="space-y-3">
              {validationDetails.pastDue?.length > 0 && (
                <div className="bg-error-10 border border-error-20 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-5 h-5 bg-error rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">!</span>
                    </div>
                    <span className="text-sm font-medium text-error-dark">{t('admin.configuration.stripe.actionRequiredImmediately')}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {validationDetails.pastDue.map((item: string, index: number) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-error-10 text-error-dark font-medium border border-error-20 break-words whitespace-normal">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {validationDetails.currentlyDue?.length > 0 && (
                <div className="bg-warning-10 border border-warning-20 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-5 h-5 bg-warning rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">⏳</span>
                    </div>
                    <span className="text-sm font-medium text-warning-dark">{t('admin.configuration.stripe.missingDocuments')}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {validationDetails.currentlyDue.map((item: string, index: number) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-warning-10 text-warning-dark font-medium border border-warning-20 break-words whitespace-normal">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {validationDetails.eventuallyDue?.length > 0 && (
                <div className="bg-primary-10 border border-primary-20 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">📅</span>
                    </div>
                    <span className="text-sm font-medium text-primary-dark">{t('admin.configuration.stripe.provideLater')}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {validationDetails.eventuallyDue.map((item: string, index: number) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-primary-10 text-primary-dark font-medium border border-primary-20 break-words whitespace-normal">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (stripeStatus === 'pending' || (stripeAccountId && stripeStatus === null)) {
    const isChargesEnabled = validationDetails?.chargesEnabled || false;
    const isPayoutsEnabled = validationDetails?.payoutsEnabled || false;
    const isDetailsSubmitted = validationDetails?.detailsSubmitted || false;
    const disabledReason = validationDetails?.disabledReason || null;

    return (
      <div className="space-y-6">
        {/* Statut principal - Design moderne et épuré */}
        <div className="bg-surface border border-border rounded-2xl p-4 sm:p-8 shadow-sm">
          {/* En-tête */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div className="flex items-start space-x-4 flex-1">
              <div className="w-14 h-14 bg-gradient-to-br from-warning to-warning-dark rounded-2xl flex items-center justify-center shadow-lg shadow-warning-20 flex-shrink-0">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                  {t('admin.configuration.stripe.incompleteConfiguration')}
                </h3>
                <p className="text-foreground-secondary text-sm sm:text-base">
                  {t('admin.configuration.stripe.additionalInfoRequired')}
                </p>
                {stripeAccountId && (
                  <p className="text-foreground-secondary font-mono text-xs mt-1 break-all">
                    ID: {stripeAccountId}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
              {/* Dashboard Express - Accessible dès qu'un compte existe */}
              {stripeAccountId && (
                <button
                  onClick={handleOpenExpressDashboard}
                  disabled={isOpeningDashboard}
                  className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-xl transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm w-full sm:w-auto"
                  title={t('admin.configuration.stripe.viewDashboard')}
                >
                  {isOpeningDashboard ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>{t('admin.configuration.stripe.opening')}</span>
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4" />
                      <span>{t('admin.configuration.stripe.dashboard')}</span>
                    </>
                  )}
                </button>
              )}
              <button
                onClick={refreshStripeData}
                disabled={isRefreshing}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-background-tertiary hover:bg-background-secondary text-foreground rounded-xl transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                {isRefreshing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-foreground-secondary border-t-transparent"></div>
                    <span>{t('admin.configuration.stripe.refreshingStatus')}</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    <span>{t('admin.configuration.stripe.refresh')}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Séparateur */}
          <div className="border-t border-border-light my-6"></div>

          {/* Statut des capacités - Design épuré */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-foreground-secondary uppercase tracking-wide mb-4">{t('admin.configuration.stripe.capabilities')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`relative overflow-hidden rounded-xl p-4 transition-all ${isChargesEnabled ? 'bg-success-10 border-2 border-success-20' : 'bg-error-10 border-2 border-error-20'}`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isChargesEnabled ? 'bg-success' : 'bg-error'}`}>
                    {isChargesEnabled ? (
                      <CheckCircle className="w-5 h-5 text-white" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <div className="text-xs font-medium text-foreground-secondary uppercase tracking-wide">{t('admin.configuration.stripe.charges')}</div>
                    <div className={`text-base font-semibold ${isChargesEnabled ? 'text-success-dark' : 'text-error-dark'}`}>
                      {isChargesEnabled ? t('admin.configuration.stripe.chargesEnabled') : t('admin.configuration.stripe.chargesDisabled')}
                    </div>
                  </div>
                </div>
              </div>

              <div className={`relative overflow-hidden rounded-xl p-4 transition-all ${isPayoutsEnabled ? 'bg-success-10 border-2 border-success-20' : 'bg-error-10 border-2 border-error-20'}`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPayoutsEnabled ? 'bg-success' : 'bg-error'}`}>
                    {isPayoutsEnabled ? (
                      <CheckCircle className="w-5 h-5 text-white" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <div className="text-xs font-medium text-foreground-secondary uppercase tracking-wide">{t('admin.configuration.stripe.payouts')}</div>
                    <div className={`text-base font-semibold ${isPayoutsEnabled ? 'text-success-dark' : 'text-error-dark'}`}>
                      {isPayoutsEnabled ? t('admin.configuration.stripe.payoutsEnabled') : t('admin.configuration.stripe.payoutsDisabled')}
                    </div>
                  </div>
                </div>
              </div>

              <div className={`relative overflow-hidden rounded-xl p-4 transition-all ${isDetailsSubmitted ? 'bg-success-10 border-2 border-success-20' : 'bg-error-10 border-2 border-error-20'}`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDetailsSubmitted ? 'bg-success' : 'bg-error'}`}>
                    {isDetailsSubmitted ? (
                      <CheckCircle className="w-5 h-5 text-white" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <div className="text-xs font-medium text-foreground-secondary uppercase tracking-wide">{t('admin.configuration.stripe.details')}</div>
                    <div className={`text-base font-semibold ${isDetailsSubmitted ? 'text-success-dark' : 'text-error-dark'}`}>
                      {isDetailsSubmitted ? t('admin.configuration.stripe.detailsSubmitted') : t('admin.configuration.stripe.detailsMissing')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Raison du blocage - Uniquement pour Super Admin */}
          {isSuperAdmin && disabledReason && (
            <>
              <div className="border-t border-border-light my-6"></div>
              <div className="bg-error-10 border border-error-20 rounded-xl p-5">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-error rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-error-dark mb-1">{t('admin.configuration.stripe.blockReason')}</h4>
                    <p className="text-sm text-error-dark font-mono">{disabledReason}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Action principale - Bouton unique pour compléter */}
        {(!isChargesEnabled || !isPayoutsEnabled || !isDetailsSubmitted) && (
          <div className="bg-primary-10 border-2 border-primary-20 rounded-xl p-4 sm:p-8 shadow-sm">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>

              <h4 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
                {t('admin.configuration.stripe.missingInfo')}
              </h4>
              <p className="text-foreground mb-6 max-w-lg mx-auto text-sm sm:text-base">
                {t('admin.configuration.stripe.missingInfoDesc')}
              </p>

              <button
                onClick={startOnboarding}
                disabled={isLoading || isCreating}
                className="inline-flex items-center justify-center space-x-3 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-primary bg-gradient-primary-hover text-white rounded-xl transition-all font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                {isLoading || isCreating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>{t('admin.configuration.stripe.opening')}</span>
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-5 h-5" />
                    <span>
                      {stripeAccountId
                        ? t('admin.configuration.stripe.continueStripeConfig', 'Reprendre la configuration sur Stripe')
                        : t('admin.configuration.stripe.completeStripeConfig')}
                    </span>
                  </>
                )}
              </button>

              <p className="text-xs sm:text-sm text-foreground-secondary mt-4">
                {t('admin.configuration.stripe.redirectInfo')}
              </p>
            </div>
          </div>
        )}

        {/* Détails techniques */}
        {validationDetails && (validationDetails.currentlyDue?.length > 0 || validationDetails.pastDue?.length > 0 || validationDetails.eventuallyDue?.length > 0) && (
          <div className="bg-surface rounded-xl border border-border p-6 shadow-sm">
            <div className="flex items-center space-x-2 mb-4">
              <Shield className="w-5 h-5 text-primary" />
              <h4 className="text-lg font-semibold text-foreground">{t('admin.configuration.stripe.technicalDetails')}</h4>
            </div>
            <div className="space-y-3">
              {/* Actions urgentes - Ouvert par défaut */}
              {validationDetails.pastDue?.length > 0 && (
                <div className="bg-error-10 border border-error-20 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowPastDue(!showPastDue)}
                    className="w-full flex items-center justify-between p-4 hover:bg-error-20 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-5 h-5 text-error" />
                      <span className="text-sm font-semibold text-error-dark">
                        {t('admin.configuration.stripe.pastDue', { count: validationDetails.pastDue.length })}
                      </span>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-error transition-transform ${showPastDue ? 'rotate-180' : ''}`} />
                  </button>
                  {showPastDue && (
                    <div className="p-4 pt-0">
                      <div className="flex flex-wrap gap-2">
                        {validationDetails.pastDue.map((item: string, index: number) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-error-10 text-error-dark font-medium border border-error-20 break-words whitespace-normal">
                            {translateStripeElement(item)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Documents manquants - Fermé par défaut */}
              {validationDetails.currentlyDue?.length > 0 && (
                <div className="bg-warning-10 border border-warning-20 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowCurrentlyDue(!showCurrentlyDue)}
                    className="w-full flex items-center justify-between p-4 hover:bg-warning-20 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <Clock className="w-5 h-5 text-warning" />
                      <span className="text-sm font-semibold text-warning-dark">
                        {t('admin.configuration.stripe.currentlyDue', { count: validationDetails.currentlyDue.length })}
                      </span>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-warning transition-transform ${showCurrentlyDue ? 'rotate-180' : ''}`} />
                  </button>
                  {showCurrentlyDue && (
                    <div className="p-4 pt-0">
                      <div className="flex flex-wrap gap-2">
                        {validationDetails.currentlyDue.map((item: string, index: number) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-warning-10 text-warning-dark font-medium border border-warning-20 break-words whitespace-normal">
                            {translateStripeElement(item)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* À fournir plus tard - Fermé par défaut */}
              {validationDetails.eventuallyDue?.length > 0 && (
                <div className="bg-primary-10 border border-primary-20 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowEventuallyDue(!showEventuallyDue)}
                    className="w-full flex items-center justify-between p-4 hover:bg-primary-20 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <Clock className="w-5 h-5 text-primary" />
                      <span className="text-sm font-semibold text-primary-dark">
                        {t('admin.configuration.stripe.eventuallyDue', { count: validationDetails.eventuallyDue.length })}
                      </span>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-primary transition-transform ${showEventuallyDue ? 'rotate-180' : ''}`} />
                  </button>
                  {showEventuallyDue && (
                    <div className="p-4 pt-0">
                      <div className="flex flex-wrap gap-2">
                        {validationDetails.eventuallyDue.map((item: string, index: number) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-primary-10 text-primary-dark font-medium border border-primary-20 break-words whitespace-normal">
                            {translateStripeElement(item)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Si aucun accountId Stripe n'est trouvé, afficher le formulaire de configuration
  if (!configuration?.stripeConfig?.stripeAccountId) {
    return (
      <div className="bg-surface rounded-xl border border-border p-4 sm:p-8 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-4 mb-6 sm:mb-8">
          <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center shadow-lg cursor-pointer flex-shrink-0">
            <CheckCircle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl sm:text-2xl font-bold text-foreground">
              {t('admin.configuration.stripe.configTitle')}
            </h3>
            <p className="text-foreground-secondary text-sm sm:text-base">
              {t('admin.configuration.stripe.configDesc')}
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-error-10 border border-error-20 text-error-dark px-4 py-3 rounded-lg mb-6">
            <strong className="font-semibold">{t('admin.configuration.stripe.errorTitle')}</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
        )}

        <div className="space-y-6">
          <div className="bg-primary-10 border border-primary-20 rounded-xl p-6">
            <h4 className="font-semibold text-foreground mb-3 flex items-center">
              <span className="w-6 h-6 bg-success rounded-full flex items-center justify-center mr-3">
                <span className="text-white text-xs">✓</span>
              </span>
              {t('admin.configuration.stripe.secureConfigTitle')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-foreground">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span>{t('admin.configuration.stripe.securePayments')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span>{t('admin.configuration.stripe.bankInfoRequired')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span>{t('admin.configuration.stripe.quickVerification')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span>{t('admin.configuration.stripe.autoConfirmEmail')}</span>
              </div>
            </div>
          </div>

          <button
            onClick={handleStartOnboardingClick}
            disabled={isLoading || isCreating}
            className="w-full flex items-center justify-center space-x-3 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-primary bg-gradient-primary-hover text-white rounded-xl transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm sm:text-base shadow-lg hover:shadow-xl"
          >
            {isLoading || isCreating ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t('admin.configuration.stripe.creating')}</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>{t('admin.configuration.stripe.startStripeConfig')}</span>
              </>
            )}
          </button>
        </div>

        {/* Modale de confirmation */}
        {showConfirmationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
              <div className="p-4 sm:p-6">
                {/* En-tête */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-warning-10 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-warning" />
                    </div>
                    <h3 className="text-2xl font-bold text-foreground">
                      {t('admin.configuration.stripe.confirmationTitle')}
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowConfirmationModal(false)}
                    className="text-foreground-secondary hover:text-foreground transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Avertissement irréversible */}
                <div className="bg-error-10 border border-error-20 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-error mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-error-dark mb-2">
                        {t('admin.configuration.stripe.irreversibleAction')}
                      </h4>
                      <p className="text-sm text-error-dark" dangerouslySetInnerHTML={{ __html: t('admin.configuration.stripe.irreversibleWarning') }} />
                    </div>
                  </div>
                </div>

                {/* Informations qui seront utilisées */}
                <div className="mb-6">
                  <h4 className="font-semibold text-foreground mb-4">
                    {t('admin.configuration.stripe.infoWillBeUsed')}
                  </h4>
                  <div className="bg-background-tertiary rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-foreground-secondary font-medium">{t('admin.configuration.stripe.organizationName')}</span>
                      <span className="text-foreground font-semibold">{configuration?.name || t('admin.configuration.stripe.notDefined')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-secondary font-medium">{t('admin.configuration.general.email')}:</span>
                      <span className="text-foreground font-semibold">{configuration?.email || `onboarding-${organizationId}@example.com`}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-secondary font-medium">{t('admin.configuration.stripe.country')}</span>
                      <span className="text-foreground font-semibold">
                        {configuration?.legalInfo?.address?.country || configuration?.address?.country || 'FR'}
                        {(configuration?.legalInfo?.address?.country || configuration?.address?.country) === 'FR' ? ' (France)' : ''}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-secondary font-medium">{t('admin.configuration.stripe.accountType')}</span>
                      <span className="text-foreground font-semibold">{t('admin.configuration.stripe.company')}</span>
                    </div>
                    {(configuration?.legalInfo?.address || configuration?.address) && (
                      <div className="pt-3 border-t border-border">
                        <span className="text-foreground-secondary font-medium block mb-2">{t('admin.configuration.stripe.fullAddress')}</span>
                        <span className="text-foreground">
                          {(configuration?.legalInfo?.address?.street || configuration?.address?.street) && `${configuration?.legalInfo?.address?.street || configuration?.address?.street}, `}
                          {(configuration?.legalInfo?.address?.postalCode || configuration?.address?.postalCode) && `${configuration?.legalInfo?.address?.postalCode || configuration?.address?.postalCode} `}
                          {(configuration?.legalInfo?.address?.city || configuration?.address?.city) && (configuration?.legalInfo?.address?.city || configuration?.address?.city)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Processus à suivre */}
                <div className="bg-primary-10 border border-primary-20 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-primary-dark mb-2">{t('admin.configuration.stripe.processToFollow')}</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-primary-dark">
                    <li>{t('admin.configuration.stripe.processStep1')}</li>
                    <li>{t('admin.configuration.stripe.processStep2')}</li>
                    <li>{t('admin.configuration.stripe.processStep3')}</li>
                    <li>{t('admin.configuration.stripe.processStep4')}</li>
                    <li>{t('admin.configuration.stripe.processStep5')}</li>
                  </ol>
                </div>

                {/* Boutons d'action */}
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <button
                    onClick={() => setShowConfirmationModal(false)}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-background-tertiary text-foreground rounded-lg hover:bg-background-secondary transition-colors font-medium w-full sm:w-auto text-sm sm:text-base"
                  >
                    {t('admin.configuration.stripe.cancel')}
                  </button>
                  <button
                    onClick={startOnboarding}
                    disabled={isLoading || isCreating}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 w-full sm:w-auto text-sm sm:text-base"
                  >
                    {isLoading || isCreating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{t('admin.configuration.stripe.creating')}</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>{t('admin.configuration.stripe.confirmCreate')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-xl border border-border p-4 sm:p-8 shadow-sm">
      <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-4 mb-6 sm:mb-8">
        <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center shadow-lg cursor-pointer flex-shrink-0">
          <CheckCircle className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl sm:text-2xl font-bold text-foreground">
            {t('admin.configuration.stripe.configTitle')}
          </h3>
          <p className="text-foreground-secondary text-sm sm:text-base">
            {t('admin.configuration.stripe.configDesc')}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-error-10 border border-error-20 text-error-dark px-4 py-3 rounded-lg mb-6">
          <strong className="font-semibold">{t('admin.configuration.stripe.errorTitle')}</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}

      <div className="space-y-6">
        <div className="bg-primary-10 border border-primary-20 rounded-xl p-6">
          <h4 className="font-semibold text-foreground mb-3 flex items-center">
            <span className="w-6 h-6 bg-success rounded-full flex items-center justify-center mr-3">
              <span className="text-white text-xs">✓</span>
            </span>
            {t('admin.configuration.stripe.secureConfigTitle')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-foreground">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span>{t('admin.configuration.stripe.securePayments')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span>{t('admin.configuration.stripe.bankInfoRequired')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span>{t('admin.configuration.stripe.quickVerification')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span>{t('admin.configuration.stripe.autoConfirmEmail')}</span>
            </div>
          </div>
        </div>

        <button
          onClick={handleStartOnboardingClick}
          disabled={isLoading || isCreating}
          className="w-full flex items-center justify-center space-x-3 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-primary bg-gradient-primary-hover text-white rounded-xl transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm sm:text-base shadow-lg hover:shadow-xl"
        >
          {isLoading || isCreating ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>{t('admin.configuration.stripe.creating')}</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              <span>{t('admin.configuration.stripe.startStripeConfig')}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default StripeHostedOnboardingSimple;
